import { useDeferredValue, useMemo } from 'react'
import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import PlayCard from './PlayCard'
import { PlayListView } from './PlayListView'
import { VIEW_MODE_GRID } from './constants/Playbook'
import { ButtonTabs } from '@/components/ui/ButtonTabs'
import { InfiniteScrollSentinel } from '@/components/ui/InfiniteScrollSentinel'
import { SelectionActionBar } from '@/components/shared/SelectionActionBar'
import { LabelFilterBar } from '@/components/labels/LabelFilterBar'
import { usePlaybookLabels, UNTAGGED_FILTER } from '@/hooks/UsePlaybookLabels'
import { TRASH_RETENTION_DAYS } from '@/constants/Trash'
import type { EditorTab } from './Types'
import type { Play } from '@/hooks/UsePlaybookData'

interface PlaysTabProps {
	playbookId: string
	plays: Play[]
	trashedPlays: Play[]
	viewMode: 'grid' | 'list'
	searchQuery: string
	selectedPlays: Set<string>
	isTrashView: boolean
	activeTab: EditorTab
	onTabChange: (tab: EditorTab) => void
	tabOptions: Array<{ value: EditorTab; label: string }>
	onNewPlay: () => void
	onOpenPlay: (playId: string) => void
	onAnimatePlay: (playId: string) => void
	onRenamePlay: (playId: string) => void
	onDeletePlay: (playId: string) => void
	onRestorePlay: (playId: string) => void
	onDuplicatePlay: (playId: string) => void
	onTogglePlaySelection: (playId: string) => void
	onSelectAllVisible: () => void
	onTrashToggle: () => void
	onBatchDelete: () => void
	onBatchRestore: () => void
	onBatchPermanentDelete: () => void
	onClearSelection: () => void
	// Pagination props
	onLoadMore?: () => void
	hasMore?: boolean
	isLoadingMore?: boolean
}

export function PlaysTab({
							 playbookId,
							 plays,
							 trashedPlays,
							 viewMode,
							 searchQuery,
							 selectedPlays,
							 isTrashView,
							 activeTab,
							 onTabChange,
							 tabOptions,
							 onNewPlay,
							 onOpenPlay,
							 onAnimatePlay,
							 onRenamePlay,
							 onDeletePlay,
							 onRestorePlay,
							 onDuplicatePlay,
							 onTogglePlaySelection,
							 onSelectAllVisible,
							 onTrashToggle,
							 onBatchDelete,
							 onBatchRestore,
							 onBatchPermanentDelete,
							 onClearSelection,
							 onLoadMore,
							 hasMore,
							 isLoadingMore,
						 }: PlaysTabProps) {
	const deferredSearchQuery = useDeferredValue(searchQuery)

	// Label filtering
	const {
		labels: playbookLabels,
		activeFilter: activeLabelFilter,
		setActiveFilter: setActiveLabelFilter,
		createLabel,
		reorderLabels,
	} = usePlaybookLabels(playbookId)

	// Compute label counts from all plays
	const { counts: labelCounts, untaggedCount } = useMemo(() => {
		const c: Record<string, number> = {}
		let untagged = 0
		for (const play of plays) {
			if (!play.labelObjects || play.labelObjects.length === 0) {
				untagged++
			} else {
				for (const label of play.labelObjects) {
					c[label.id] = (c[label.id] || 0) + 1
				}
			}
		}
		return { counts: c, untaggedCount: untagged }
	}, [plays])

	// Apply label filter, then search filter
	const filteredPlays = useMemo(() => {
		// Label filter
		let filtered = plays
		if (activeLabelFilter) {
			if (activeLabelFilter === UNTAGGED_FILTER) {
				filtered = filtered.filter(p => !p.labelObjects || p.labelObjects.length === 0)
			} else {
				filtered = filtered.filter(p =>
					p.labelObjects?.some(l => l.id === activeLabelFilter),
				)
			}
		}

		// Search filter
		if (deferredSearchQuery) {
			const q = deferredSearchQuery.toLowerCase()
			filtered = filtered.filter(
				(play) =>
					play.name.toLowerCase().includes(q) ||
					play.formation.toLowerCase().includes(q) ||
					play.labels.some((label) => label.toLowerCase().includes(q)),
			)
		}

		return filtered
	}, [plays, activeLabelFilter, deferredSearchQuery])

	// Filter trashed plays by search query
	const filteredTrashedPlays = useMemo(
		() => (trashedPlays || []).filter(
			(play) => {
				if (!deferredSearchQuery) return true
				const q = deferredSearchQuery.toLowerCase()
				return (
					play.name.toLowerCase().includes(q) ||
					play.formation.toLowerCase().includes(q) ||
					play.labels.some((label) => label.toLowerCase().includes(q))
				)
			},
		),
		[trashedPlays, deferredSearchQuery],
	)

	const handleLabelFilterChange = (filter: string | null) => {
		// When selecting a label filter, clear the trash view
		if (isTrashView) {
			onTrashToggle()
		}
		setActiveLabelFilter(filter)
	}

	const handleTrashClick = () => {
		setActiveLabelFilter(null)
		onTrashToggle()
	}

	return (
		<>
			{/* Toolbar */}
			<div className="playbook-editor-tabs">
				<div className="playbook-editor-tabs-content">
					<ButtonTabs
						options={tabOptions}
						value={activeTab}
						onChange={onTabChange}
						aria-label="Content type"
					/>

					<div className="playbook-editor-tabs-divider" />

					<div className="playbook-editor-toolbar-buttons">
						<button
							onClick={onNewPlay}
							className="playbook-editor-action-button playbook-editor-action-button--primary"
						>
							<Plus className="icon-sm" />
							<span>New Play</span>
						</button>
					</div>

					<div className="playbook-editor-tabs-divider" />

					<LabelFilterBar
						labels={playbookLabels}
						counts={labelCounts}
						totalCount={plays.length}
						untaggedCount={untaggedCount}
						activeFilter={isTrashView ? null : activeLabelFilter}
						isExternalViewActive={isTrashView}
						onFilterChange={handleLabelFilterChange}
						onCreateLabel={async (data) => { await createLabel(data) }}
						onReorder={reorderLabels}
					/>

					{/* Trash toggle */}
					<button
						onClick={handleTrashClick}
						className="label-filter-pill label-filter-pill-untagged"
						data-active={isTrashView}
						aria-pressed={isTrashView}
					>
						<Trash2 size={12} />
						Trash
					</button>
				</div>
			</div>

			{/* Content */}
			<div className="playbook-editor-content">
				{/* Trash Section View */}
				{isTrashView ? (
					<>
						{filteredTrashedPlays.length > 0 ? (
							<div className="playbook-editor-sections">
								<div>
									<div className="playbook-editor-section-header">
										<h2>Trash</h2>
										<div className="playbook-editor-section-meta">
											<p className="playbook-editor-section-count">
												{filteredTrashedPlays.length} play
												{filteredTrashedPlays.length != 1 ? 's' : ''}
											</p>
											<span className="playbook-editor-trash-notice">
												Items in trash will be permanently deleted after {TRASH_RETENTION_DAYS} days.
											</span>
										</div>
									</div>

									{viewMode == VIEW_MODE_GRID ? (
										<div className="playbook-editor-grid">
											{filteredTrashedPlays.map((play, index) => (
												<PlayCard
													key={play.id}
													{...play}
													animateIndex={index}
													selected={selectedPlays.has(play.id)}
													onSelect={() => onTogglePlaySelection(play.id)}
													onOpen={onOpenPlay}
													onAnimate={onAnimatePlay}
													onRename={onRenamePlay}
													onDelete={onRestorePlay}
													onDuplicate={onDuplicatePlay}
												/>
											))}
										</div>
									) : (
										<PlayListView
											plays={filteredTrashedPlays}
											selectedPlays={selectedPlays}
											visiblePlayIds={filteredTrashedPlays.map(p => p.id)}
											onSelect={onTogglePlaySelection}
											onSelectAllVisible={onSelectAllVisible}
											onOpen={onOpenPlay}
											onAnimate={onAnimatePlay}
											onRename={onRenamePlay}
											onDelete={onRestorePlay}
											onDuplicate={onDuplicatePlay}
										/>
									)}
								</div>
							</div>
						) : (
							<div className="playbook-editor-empty">
								<Trash2 className="playbook-editor-empty-icon" />
								<p className="playbook-editor-empty-text">
									{deferredSearchQuery
										? 'No trashed plays found matching your search'
										: 'Trash is empty'
									}
								</p>
							</div>
						)}
					</>
				) : (
					<>
						{/* Flat label-filtered plays view */}
						{filteredPlays.length > 0 ? (
							<div className="playbook-editor-sections">
								<div>
									<div className="playbook-editor-section-header">
										<h2>Plays</h2>
										<p className="playbook-editor-section-count">
											{filteredPlays.length} play
											{filteredPlays.length !== 1 ? 's' : ''}
										</p>
									</div>

									{viewMode == VIEW_MODE_GRID ? (
										<div className="playbook-editor-grid">
											{filteredPlays.map((play, index) => (
												<PlayCard
													key={play.id}
													{...play}
													animateIndex={index}
													selected={selectedPlays.has(play.id)}
													onSelect={() => onTogglePlaySelection(play.id)}
													onOpen={onOpenPlay}
													onAnimate={onAnimatePlay}
													onRename={onRenamePlay}
													onDelete={onDeletePlay}
													onDuplicate={onDuplicatePlay}
												/>
											))}
										</div>
									) : (
										<PlayListView
											plays={filteredPlays}
											selectedPlays={selectedPlays}
											visiblePlayIds={filteredPlays.map(p => p.id)}
											onSelect={onTogglePlaySelection}
											onSelectAllVisible={onSelectAllVisible}
											onOpen={onOpenPlay}
											onAnimate={onAnimatePlay}
											onRename={onRenamePlay}
											onDelete={onDeletePlay}
											onDuplicate={onDuplicatePlay}
										/>
									)}
								</div>
							</div>
						) : null}

						{/* Infinite scroll sentinel */}
						{filteredPlays.length > 0 && onLoadMore && (
							<InfiniteScrollSentinel
								onLoadMore={onLoadMore}
								hasMore={hasMore ?? false}
								isLoading={isLoadingMore ?? false}
							/>
						)}

						{filteredPlays.length === 0 && (
							<div className="playbook-editor-empty">
								<p className="playbook-editor-empty-text">
									{deferredSearchQuery || activeLabelFilter
										? 'No plays found matching your filters'
										: 'No plays in this playbook'
									}
								</p>
								{!deferredSearchQuery && !activeLabelFilter && (
									<button
										onClick={onNewPlay}
										className="playbook-editor-empty-button"
									>
										Create Your First Play
									</button>
								)}
							</div>
						)}
					</>
				)}
			</div>

			{/* Selection Action Bar - Dynamic actions based on view */}
			{selectedPlays.size > 0 && (
				<SelectionActionBar
					selectedCount={selectedPlays.size}
					onClearSelection={onClearSelection}
					actions={isTrashView
						? [
							{
								label: 'Restore',
								icon: <RotateCcw size={18} />,
								onClick: onBatchRestore,
							},
							{
								label: 'Delete Permanently',
								icon: <Trash2 size={18} />,
								onClick: onBatchPermanentDelete,
								variant: 'destructive' as const,
							},
						]
						: [
							{
								label: 'Delete',
								icon: <Trash2 size={18} />,
								onClick: onBatchDelete,
								variant: 'destructive' as const,
							},
						]
					}
				/>
			)}
		</>
	)
}
